import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import Swal from "sweetalert2";

const playScanBeep = () => {
  const ctx = new (window.AudioContext || window.webkitAudioContext)();

  const beep = (time) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.type = "sine";
    osc.frequency.value = 950;

    gain.gain.setValueAtTime(0.25, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + 0.08);

    osc.start(time);
    osc.stop(time + 0.08);
  };

  const now = ctx.currentTime;

  // 🔊 double beep
  beep(now);
  beep(now + 0.12);
};

const playErrorBeep = () => {
  const ctx = new (window.AudioContext || window.webkitAudioContext)();

  const osc = ctx.createOscillator();
  const gain = ctx.createGain();

  osc.connect(gain);
  gain.connect(ctx.destination);

  osc.type = "square"; // harsher sound for error
  osc.frequency.setValueAtTime(600, ctx.currentTime);
  osc.frequency.exponentialRampToValueAtTime(200, ctx.currentTime + 0.25);

  gain.gain.setValueAtTime(0.3, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);

  osc.start();
  osc.stop(ctx.currentTime + 0.25);
};

export default function AdminScanPlayerModal({
  show,
  onClose,
  onScanSuccess
}) {
  const scannerRef = useRef(null);
  const cooldown = useRef(false);
  const [loading, setLoading] = useState(false);

  const startScanner = async () => {
    if (!scannerRef.current) return;

    try {
      await scannerRef.current.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: 250 },
        async (decodedText) => {
          if (cooldown.current) return;

          cooldown.current = true;
          setLoading(true);

          try {
            const data = JSON.parse(decodedText);
            if (!data.uid) throw new Error();

            await scannerRef.current.stop();

            playScanBeep();
            onScanSuccess(data);

            // ⏳ short pause → restart
            setTimeout(async () => {
              setLoading(false);
              cooldown.current = false;
              await startScanner();
            }, 3000);

          } catch {
            
            playErrorBeep();
            if (!Swal.isVisible()) {
              Swal.fire("Invalid QR", "This is not a player QR", "error");
            }

            setTimeout(() => {
              setLoading(false);
              cooldown.current = false;
            }, 2000);
          }
        }
      );
    } catch {
      // camera already running or modal closing
    }
  };

  const stopScanner = async () => {
    try {
      const state = scannerRef.current?.getState?.();
      if (state && state !== "NOT_STARTED") {
        await scannerRef.current.stop();
      }
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    if (!show) return;

    scannerRef.current = new Html5Qrcode("admin-player-scanner");
    startScanner();

    return () => {
      stopScanner();
      scannerRef.current = null;
    };
  }, [show]);

  if (!show) return null;

  return (
    <>
      <div className="modal-backdrop fade show" onClick={onClose} />

      <div className="modal d-block" style={{ zIndex: 1059 }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content rounded-4">

            <div className="modal-header border-0">
              <h5 className="modal-title fw-bold">Scan Player QR</h5>
              <button className="btn-close" onClick={onClose} />
            </div>

            <div className="modal-body text-center">
              <div
                id="admin-player-scanner"
                style={{ width: "100%" }}
              />

              {loading && (
                <div className="mt-3">
                  <div className="spinner-border spinner-border-sm text-primary" />
                  <div className="small text-muted mt-2">
                    Adding player…
                  </div>
                </div>
              )}

              {!loading && (
                <p className="text-muted mt-3 mb-0 small">
                  Scan player’s QR to add them to the queue
                </p>
              )}
            </div>

          </div>
        </div>
      </div>
    </>
  );
}
